import React, { useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { exchangeCodeForToken, getDefaultRedirectPathByUserType } from '../services/api';

// Animations
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled components
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: var(--color-bg-secondary);
  padding: 2rem;
`;

const LoadingBox = styled.div`
  background: var(--color-bg-primary);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
  max-width: 400px;
  width: 100%;
  animation: ${fadeIn} 0.6s ease-out;
`;

const Spinner = styled.div`
  display: inline-block;
  width: 50px;
  height: 50px;
  border: 4px solid var(--color-bg-tertiary);
  border-radius: 50%;
  border-top-color: var(--color-text-primary);
  animation: ${spin} 1s linear infinite;
  margin-bottom: 1.5rem;
`;

const LoadingText = styled.p`
  color: var(--color-text-primary);
  font-size: 1.1rem;
  margin: 0;
  font-weight: 500;
  line-height: 1.6;
  
  strong {
    color: var(--color-text-secondary);
    font-weight: 600;
  }
`;

const ErrorMessage = styled.div`
  color: #d63031;
  background: var(--color-bg-tertiary);
  padding: 1rem;
  border-radius: 12px;
  margin-top: 1.5rem;
  font-weight: 500;
  border: 1px solid #ffcdd2;
`;

const CallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setError, fetchTickets } = useApp();

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      if (error) {
        console.error('Auth error:', error, errorDescription);
        setError(errorDescription || 'Erro durante autenticação');
        navigate('/login');
        return;
      }

      if (!code) {
        const state = params.get('state');
        if (state !== 'logout') {
          console.error('No code received');
          setError('Código de autorização não recebido');
        }
        navigate('/login');
        return;
      }

      try {
        await exchangeCodeForToken(code);
        await fetchTickets(true, 1);
        // Aguarda o contexto de autenticação ser atualizado antes de redirecionar
        setTimeout(() => {
          const savedRedirect = localStorage.getItem('postLoginRedirect');
          const redirectPath = savedRedirect || getDefaultRedirectPathByUserType();
          if (savedRedirect) localStorage.removeItem('postLoginRedirect');
          navigate(redirectPath);
        }, 100); // Pequeno delay para garantir atualização do contexto
      } catch (err) {
        console.error('Error exchanging code for token:', err);
        setError('Erro ao processar autenticação');
        navigate('/login');
      }
    };

    handleCallback();
  }, [location]);

  return (
    <LoadingContainer>
      <LoadingBox>
        <Spinner />
        <LoadingText>
          <strong>Processando autenticação</strong><br />
          Aguarde enquanto conectamos você ao sistema...
        </LoadingText>
        
        {/* Mensagem de erro opcional caso queira exibir temporariamente */}
        {/* <ErrorMessage>Erro de autenticação</ErrorMessage> */}
      </LoadingBox>
    </LoadingContainer>
  );
};

export default CallbackPage;